import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, FilterFrame, FilterSelection } from '../Filters.style';
import { FilterOption, FilterResultCounts, RangeResultsCounts } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import FilterExposedHeader from '../FilterExposedHeader';
import { ExteriorListWrapper, OptionWrapper, Image, Label } from './ExteriorSelector.style';
import useAnimations from '../../../hooks/Animations/useAnimations';

interface ExteriorSelectorTypes {
  filterType: string;
  exteriorColorOptions: {
    [key: string]: FilterOption;
  };
  temporaryFilters: FilterTypes;
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string) => void;
  handleExposeFilter: (arg: string) => void;
}

const ExteriorSelector: React.FC<ExteriorSelectorTypes> = ({
  filterType,
  exteriorColorOptions,
  temporaryFilters,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
}) => {
  const { t } = useTranslation();

  const selectedExteriorColorOptions = useMemo(() => {
    const seletedExteriorColorsList = temporaryFilters.exteriorColor;
    return seletedExteriorColorsList.map((color) => exteriorColorOptions[color].label).join(', ');
  }, [exteriorColorOptions, temporaryFilters.exteriorColor]);

  const selectedExteriorColors = useMemo(() => {
    return temporaryFilters.exteriorColor;
  }, [temporaryFilters.exteriorColor]);

  const handleAddExteriorFilter = (newExteriorColor: string) => {
    handleAddFilter('exteriorColor', newExteriorColor);
  };

  const showFilter = openedFiltersList[filterType];

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  return (
    <Filter>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedExteriorColorOptions}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props} data-testid="exterior-filter-group">
              <FilterFrame style={filtersAccordianAnimation}>
                <ExteriorListWrapper>
                  {Object.keys(exteriorColorOptions).map((filterOption) => {
                    const option = exteriorColorOptions[filterOption];
                    const { value, image, ariaLabel, label } = option;
                    const isSelected = selectedExteriorColors.includes(value);
                    return (
                      <OptionWrapper
                        key={`ext-${value}`}
                        type="button"
                        onClick={() => handleAddExteriorFilter(value)}
                        isSelected={isSelected}
                        data-testid={`exterior-select-${value}`}
                      >
                        <Image
                          src={image as string}
                          aria-label={`${ariaLabel} exterior color`}
                          alt={`${ariaLabel} exterior color`}
                        />
                        <Label>{label}</Label>
                      </OptionWrapper>
                    );
                  })}
                </ExteriorListWrapper>
              </FilterFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default ExteriorSelector;
